// 作者: Codex
import { readTiandituPublicToken } from "../utils/runtimeConfig";

const TIANDITU_SEARCH_URL = "https://api.tianditu.gov.cn/v2/search";
const TIANDITU_ADMINISTRATIVE_URL =
    "https://api.tianditu.gov.cn/v2/administrative";
const GLOBAL_MAP_BOUND = "-180,-90,180,90";
const DEFAULT_SEARCH_LEVEL = 12;
const DEFAULT_SUGGESTION_LIMIT = 10;
const RECOMMENDED_POI_KEYWORDS = [
    "人民政府",
    "医院",
    "学校",
    "村委会",
    "镇政府",
    "道路",
] as const;
const ADMIN_SCOPE_SUFFIXES = [
    "自治区",
    "自治州",
    "自治县",
    "自治旗",
    "地区",
    "省",
    "市",
    "县",
    "区",
    "盟",
    "旗",
] as const;
const ADMIN_SEGMENT_SUFFIXES = [
    ...ADMIN_SCOPE_SUFFIXES,
    "街道",
    "苏木",
    "镇",
    "乡",
] as const;
const KEYWORD_SEPARATOR_PATTERN = /^[\s,，、;；/]+$/;

type TiandituSearchPoi = {
    name?: string;
    address?: string;
    province?: string;
    city?: string;
    county?: string;
    lon?: string;
    lng?: string;
    lat?: string;
    lonlat?: string;
    poiType?: string | number;
};

type TiandituSearchResponse = {
    pois?: TiandituSearchPoi[];
};

type TiandituAdministrativeDistrict = {
    name?: string;
    adminCode?: string | number;
    code?: string | number;
    gb?: string | number;
};

type TiandituAdministrativeResponse = {
    data?:
        | TiandituAdministrativeDistrict[]
        | {
              district?: TiandituAdministrativeDistrict[];
              districts?: TiandituAdministrativeDistrict[];
              suggestion?: TiandituAdministrativeDistrict[];
          };
    district?: TiandituAdministrativeDistrict[];
    districts?: TiandituAdministrativeDistrict[];
};

interface AdministrativeSegment {
    text: string;
    start: number;
    end: number;
}

interface AdministrativeArea {
    name: string;
    adminCode: string;
    matchedTerm: string;
}

interface SearchPoisOptions {
    keyword: string;
    token: string;
    limit: number;
    area?: AdministrativeArea;
}

export interface TiandituLocationSuggestion {
    id: string;
    name: string;
    address: string;
    province?: string;
    city?: string;
    county?: string;
    lng: number;
    lat: number;
    sourceKeyword: string;
}

export interface SearchTiandituLocationOptions {
    limit?: number;
}

const normalizeKeyword = (keyword: string): string => keyword.trim();

const createTiandituError = (message: string): Error =>
    new Error(`天地图位置联想失败：${message}`);

const isSeparator = (value: string): boolean =>
    KEYWORD_SEPARATOR_PATTERN.test(value);

const findNextAdminSuffix = (
    keyword: string,
    startIndex: number,
): { index: number; suffix: string } | null => {
    for (let index = startIndex; index < keyword.length; index += 1) {
        const suffix = ADMIN_SEGMENT_SUFFIXES.find((item) =>
            keyword.startsWith(item, index),
        );

        if (suffix) {
            return { index, suffix };
        }
    }

    return null;
};

const extractAdministrativeSegments = (
    keyword: string,
): AdministrativeSegment[] => {
    const segments: AdministrativeSegment[] = [];
    let start = 0;

    while (start < keyword.length) {
        while (start < keyword.length && isSeparator(keyword[start])) {
            start += 1;
        }

        const match = findNextAdminSuffix(keyword, start);
        if (!match) {
            break;
        }

        const end = match.index + match.suffix.length;
        const text = keyword.slice(start, end);

        if (text.length > match.suffix.length) {
            segments.push({ text, start, end });
        }

        start = end;
    }

    return segments;
};

const isScopeAdministrativeSegment = (segment: string): boolean =>
    ADMIN_SCOPE_SUFFIXES.some((suffix) => segment.endsWith(suffix));

const getAdministrativeScopeCandidates = (
    keyword: string,
): AdministrativeSegment[] => {
    const candidates = extractAdministrativeSegments(keyword)
        .filter((segment) => isScopeAdministrativeSegment(segment.text))
        .reverse();
    const normalizedKeyword = normalizeKeyword(keyword);

    if (normalizedKeyword) {
        candidates.push({
            text: normalizedKeyword,
            start: 0,
            end: normalizedKeyword.length,
        });
    }

    const seenCandidateTexts = new Set<string>();
    return candidates.filter((candidate) => {
        if (seenCandidateTexts.has(candidate.text)) {
            return false;
        }

        seenCandidateTexts.add(candidate.text);
        return true;
    });
};

const getSpecificKeywordAfterArea = (
    keyword: string,
    area: AdministrativeArea,
): string => {
    const areaIndex = keyword.lastIndexOf(area.matchedTerm);

    if (areaIndex < 0) {
        return keyword;
    }

    return keyword.slice(areaIndex + area.matchedTerm.length).trim();
};

const isAdministrativeOnlyKeyword = (keyword: string): boolean => {
    const segments = extractAdministrativeSegments(keyword);

    return segments.some(
        (segment) => segment.start === 0 && segment.end === keyword.length,
    );
};

const buildRecommendedPoiKeyword = (keyword: string, prefix = ""): string => {
    if (!prefix) {
        return keyword;
    }

    if (keyword === "镇政府" && prefix.endsWith("镇")) {
        return `${prefix}政府`;
    }

    return `${prefix}${keyword}`;
};

const buildUrl = (
    baseUrl: string,
    params: Record<string, string | number | boolean>,
): string => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        searchParams.set(key, String(value));
    });

    return `${baseUrl}?${searchParams.toString()}`;
};

const fetchJson = async <T>(url: string): Promise<T> => {
    const response = await fetch(url);

    if (!response.ok) {
        throw createTiandituError(`接口返回 ${response.status}`);
    }

    return (await response.json()) as T;
};

const readAdministrativeDistricts = (
    response: TiandituAdministrativeResponse,
): TiandituAdministrativeDistrict[] => {
    if (Array.isArray(response.data)) {
        return response.data;
    }

    return (
        [
            response.data?.district,
            response.data?.districts,
            response.data?.suggestion,
            response.district,
            response.districts,
        ].find((items): items is TiandituAdministrativeDistrict[] =>
            Array.isArray(items),
        ) ?? []
    );
};

const getDistrictAdminCode = (
    district: TiandituAdministrativeDistrict,
): string => {
    const code = district.adminCode ?? district.code ?? district.gb;
    return code === undefined ? "" : String(code).trim();
};

const resolveAdministrativeArea = async (
    keyword: string,
    token: string,
): Promise<AdministrativeArea | null> => {
    const candidates = getAdministrativeScopeCandidates(keyword);

    for (const candidate of candidates) {
        const url = buildUrl(TIANDITU_ADMINISTRATIVE_URL, {
            keyword: candidate.text,
            childLevel: 0,
            extensions: false,
            tk: token,
        });
        const response = await fetchJson<TiandituAdministrativeResponse>(url);
        const districts = readAdministrativeDistricts(response);
        const district =
            districts.find((item) => item.name?.trim() === candidate.text) ??
            districts[0];
        const adminCode = district ? getDistrictAdminCode(district) : "";

        if (district?.name && adminCode) {
            return {
                name: district.name.trim(),
                adminCode,
                matchedTerm: candidate.text,
            };
        }
    }

    return null;
};

const parseLngLat = (
    lonlat?: string,
    lngValue?: string,
    latValue?: string,
): { lng: number; lat: number } | null => {
    let lng = Number.NaN;
    let lat = Number.NaN;

    if (lngValue && latValue) {
        lng = parseFloat(lngValue);
        lat = parseFloat(latValue);
    }

    if ((Number.isNaN(lng) || Number.isNaN(lat)) && lonlat) {
        const parts = lonlat.split(/[\s,]+/);
        if (parts.length >= 2) {
            lng = parseFloat(parts[0]);
            lat = parseFloat(parts[1]);
        }
    }

    if (Number.isNaN(lng) || Number.isNaN(lat)) {
        return null;
    }

    return { lng, lat };
};

const normalizePoi = (
    poi: TiandituSearchPoi,
    sourceKeyword: string,
): TiandituLocationSuggestion | null => {
    const name = poi.name?.trim();
    const lnglat = parseLngLat(poi.lonlat, poi.lon ?? poi.lng, poi.lat);

    if (!name || !lnglat) {
        return null;
    }

    const address = poi.address?.trim() ?? "";
    const id = [name, address, lnglat.lng, lnglat.lat].join("|");

    return {
        id,
        name,
        address,
        province: poi.province?.trim() || undefined,
        city: poi.city?.trim() || undefined,
        county: poi.county?.trim() || undefined,
        lng: lnglat.lng,
        lat: lnglat.lat,
        sourceKeyword,
    };
};

const isPureAdministrativeSuggestion = (
    suggestion: TiandituLocationSuggestion,
    area?: AdministrativeArea,
): boolean => {
    if (!area) {
        return false;
    }

    return suggestion.name === area.name && !suggestion.address;
};

const dedupeSuggestions = (
    suggestions: TiandituLocationSuggestion[],
): TiandituLocationSuggestion[] => {
    const seenKeys = new Set<string>();
    const dedupedSuggestions: TiandituLocationSuggestion[] = [];

    suggestions.forEach((suggestion) => {
        const key = suggestion.id;
        if (seenKeys.has(key)) {
            return;
        }

        seenKeys.add(key);
        dedupedSuggestions.push(suggestion);
    });

    return dedupedSuggestions;
};

const buildSearchPostStr = ({
    keyword,
    limit,
    area,
}: SearchPoisOptions): Record<string, string | number> => {
    if (area) {
        return {
            keyWord: keyword,
            queryType: 12,
            start: 0,
            count: limit,
            specify: area.adminCode,
            show: 2,
        };
    }

    return {
        keyWord: keyword,
        level: DEFAULT_SEARCH_LEVEL,
        mapBound: GLOBAL_MAP_BOUND,
        queryType: 1,
        start: 0,
        count: limit,
        show: 2,
    };
};

const searchPois = async (
    options: SearchPoisOptions,
): Promise<TiandituLocationSuggestion[]> => {
    const postStr = JSON.stringify(buildSearchPostStr(options));
    const url = buildUrl(TIANDITU_SEARCH_URL, {
        postStr,
        type: "query",
        tk: options.token,
    });
    const response = await fetchJson<TiandituSearchResponse>(url);
    const pois = response.pois ?? [];

    return pois
        .map((poi) => normalizePoi(poi, options.keyword))
        .filter((item): item is TiandituLocationSuggestion => Boolean(item))
        .filter((item) => !isPureAdministrativeSuggestion(item, options.area));
};

const searchRecommendedPois = async (
    area: AdministrativeArea,
    token: string,
    limit: number,
    keywordPrefix = "",
): Promise<TiandituLocationSuggestion[]> => {
    const perKeywordLimit = Math.max(
        2,
        Math.ceil(limit / RECOMMENDED_POI_KEYWORDS.length),
    );
    const settledResults = await Promise.allSettled(
        RECOMMENDED_POI_KEYWORDS.map((keyword) =>
            searchPois({
                keyword: buildRecommendedPoiKeyword(keyword, keywordPrefix),
                token,
                limit: perKeywordLimit,
                area,
            }),
        ),
    );

    return settledResults.flatMap((result) =>
        result.status === "fulfilled" ? result.value : [],
    );
};

const searchAreaSupplementalPois = async (
    normalizedKeyword: string,
    area: AdministrativeArea,
    token: string,
    limit: number,
): Promise<TiandituLocationSuggestion[]> => {
    const specificKeyword = getSpecificKeywordAfterArea(
        normalizedKeyword,
        area,
    );

    if (!specificKeyword) {
        return searchRecommendedPois(area, token, limit);
    }

    if (isAdministrativeOnlyKeyword(specificKeyword)) {
        return searchRecommendedPois(area, token, limit, specificKeyword);
    }

    return searchPois({
        keyword: specificKeyword,
        token,
        limit,
        area,
    });
};

export const searchTiandituLocationSuggestions = async (
    keyword: string,
    options: SearchTiandituLocationOptions = {},
): Promise<TiandituLocationSuggestion[]> => {
    const normalizedKeyword = normalizeKeyword(keyword);
    const limit = options.limit ?? DEFAULT_SUGGESTION_LIMIT;

    if (!normalizedKeyword) {
        return [];
    }

    const token = readTiandituPublicToken();
    if (!token) {
        throw createTiandituError("未配置浏览器端密钥");
    }

    const primarySuggestions = dedupeSuggestions(
        await searchPois({
            keyword: normalizedKeyword,
            token,
            limit,
        }),
    );

    if (primarySuggestions.length >= limit) {
        return primarySuggestions.slice(0, limit);
    }

    let area: AdministrativeArea | null = null;
    try {
        area = await resolveAdministrativeArea(normalizedKeyword, token);
    } catch (error) {
        console.warn("Tianditu administrative search failed:", error);
    }

    if (!area) {
        return primarySuggestions.slice(0, limit);
    }

    const remainingLimit = limit - primarySuggestions.length;
    const supplementalSuggestions = await searchAreaSupplementalPois(
        normalizedKeyword,
        area,
        token,
        remainingLimit,
    );

    return dedupeSuggestions([
        ...primarySuggestions,
        ...supplementalSuggestions,
    ]).slice(0, limit);
};
